import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import {
  UsersIcon,
  AcademicCapIcon,
  ClockIcon,
  CalendarIcon,
  DocumentTextIcon,
  EyeIcon,
  PlusIcon,
  FunnelIcon,
  CodeBracketIcon,
  LightBulbIcon,
  BookOpenIcon,
  TableCellsIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline'
import { RootState } from '../../store'
import DashboardCard from '../../components/DashboardCard'
import CandidateCard from '../../components/CandidateCard'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'
import * as XLSX from 'xlsx'

interface RecruiterStats {
  totalCandidates: number
  activeDrives: number
  completedAssessments: number
}

interface Drive {
  id: string
  title: string
  description: string
  status: 'upcoming' | 'active' | 'completed' | 'cancelled'
  startDate: string
  endDate: string
  maxCandidates: number
  registeredCandidates: number
  testDuration: number
  collegeType: 'engineering' | 'management'
  tier: 'tier1' | 'tier2'
  createdAt: string
  linkedAssessments?: any[]
}

interface CandidateResult {
  id: string
  name: string
  email: string
  college: string
  assessmentTitle: string
  score: number
  completedAt: string
  status: 'completed' | 'in_progress' | 'shortlisted' | 'rejected'
  skills: string[]
  proctoringFlags: number
}

const RecruiterDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)

  const [recruiterStats, setRecruiterStats] = useState<RecruiterStats>({
    totalCandidates: 0,
    activeDrives: 0,
    completedAssessments: 0
  })

  const [drives, setDrives] = useState<Drive[]>([])
  const [candidateResults, setCandidateResults] = useState<CandidateResult[]>([])
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'shortlisted' | 'pending'>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [showAssessmentModal, setShowAssessmentModal] = useState(false)
  const [showAssessmentTypeModal, setShowAssessmentTypeModal] = useState(false)
  const [showViewAssessmentModal, setShowViewAssessmentModal] = useState(false)
  const [showAssessmentDetailsModal, setShowAssessmentDetailsModal] = useState(false)
  const [showQuestionBankModal, setShowQuestionBankModal] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState<any>(null)
  const [selectedCategory, setSelectedCategory] = useState<{collegeType: 'engineering' | 'management', tier: 'tier1' | 'tier2'} | null>(null)
  const [selectedAssessmentType, setSelectedAssessmentType] = useState<string | null>(null)
  const [createdAssessments, setCreatedAssessments] = useState<any[]>([])
  const [questionBankQuestions, setQuestionBankQuestions] = useState<any[]>([])

  useEffect(() => {
    loadRecruiterData()
    loadCreatedAssessments()
  }, [])

  const updateDriveStatus = (drives: Drive[]) => {
    const now = new Date()
    return drives.map(drive => {
      const startDate = new Date(drive.startDate)
      const endDate = new Date(drive.endDate)
      
      let status = drive.status
      if (now < startDate) {
        status = 'upcoming'
      } else if (now >= startDate && now <= endDate) {
        status = 'active'
      } else if (now > endDate) {
        status = 'completed'
      }
      
      return { ...drive, status }
    })
  }

  const loadRecruiterData = async () => {
    try {
      setIsLoading(true)
      console.log('Loading all drives from API...')
      
      // Fetch all drives using pagination to avoid missing any
      const pageSize = 500
      let apiDrives: any[] = []
      let skip = 0
      let hasMore = true

      while (hasMore) {
        const response = await fetch(`/api/v1/drives/?limit=${pageSize}&skip=${skip}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token') || 'test-token'}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const pageDrives = await response.json()
        apiDrives = [...apiDrives, ...pageDrives]
        
        if (pageDrives.length < pageSize) {
          hasMore = false
        } else {
          skip += pageSize
        }
      }

      console.log(`Total drives fetched: ${apiDrives.length}`)
      
      // Transform API response to match frontend interface
      const transformedDrives: Drive[] = apiDrives.map((drive: any) => ({
        id: drive.id.toString(),
        title: drive.title,
        description: drive.description || '',
        startDate: drive.start_date,
        endDate: drive.end_date,
        maxCandidates: drive.max_candidates,
        registeredCandidates: drive.registered_candidates,
        testDuration: drive.total_duration_minutes,
        status: drive.status as 'upcoming' | 'active' | 'completed' | 'cancelled',
        createdAt: drive.created_at,
        collegeType: drive.college_type as 'engineering' | 'management',
        tier: drive.tier as 'tier1' | 'tier2',
        role: drive.role as 'ASE' | 'Cloud Support Engineer' | 'Pre-Sales' | undefined
      }))
      
      // Update status based on current date
      const updatedDrives = updateDriveStatus(transformedDrives)
      
      setDrives(updatedDrives)
      
      // Calculate stats based on actual drives
      const totalCandidates = updatedDrives.reduce((sum: number, drive: Drive) => sum + drive.registeredCandidates, 0)
      const activeDrives = updatedDrives.filter((drive: Drive) => drive.status === 'active').length
      const completedAssessments = 0 // No completed assessments yet - will be updated when actual test completion data is available
      
      setRecruiterStats({
        totalCandidates,
        activeDrives,
        completedAssessments
      })

      setCandidateResults([]) // TODO: Load actual candidate results

    } catch (error) {
      console.error('Failed to load recruiter data:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setIsLoading(false)
    }
  }

  const loadCreatedAssessments = () => {
    try {
      const savedAssessments = localStorage.getItem('createdAssessments')
      const assessments = savedAssessments ? JSON.parse(savedAssessments) : []
      setCreatedAssessments(assessments)
    } catch (error) {
      console.error('Failed to load created assessments:', error)
      setCreatedAssessments([])
    }
  }

  const loadQuestionBankQuestions = async (category: string, assessmentType: string) => {
    try {
      const response = await api.get(`/question-pools/question-bank?category=${category}&question_type=${assessmentType}`)
      setQuestionBankQuestions(response.data)
    } catch (error) {
      console.error('Error loading question bank questions:', error)
      setQuestionBankQuestions([])
    }
  }

  const handleViewQuestionBank = (category: {collegeType: 'engineering' | 'management', tier: 'tier1' | 'tier2'}, assessmentType: string) => {
    setSelectedCategory(category)
    setSelectedAssessmentType(assessmentType)
    
    // Convert to backend format
    const categoryStr = `${category.collegeType}_${category.tier}`
    const questionType = assessmentType === 'coding' ? 'coding' :
                        assessmentType === 'case-study' ? 'case_study' :
                        assessmentType === 'business-simulations' ? 'business_simulation' :
                        assessmentType === 'aptitude' ? 'aptitude' :
                        assessmentType === 'fundamentals' ? 'fundamentals' :
                        assessmentType === 'cloud_fundamentals' ? 'cloud_fundamentals' :
                        assessmentType === 'presales_fundamentals' ? 'presales_fundamentals' :
                        assessmentType === 'english_communication' ? 'english_communication' :
                        assessmentType === 'ai_fundamental' ? 'ai_fundamental' :
                        assessmentType === 'quantitative_ability' ? 'multiple_choice' :
                        assessmentType === 'logical_reasoning' ? 'multiple_choice' :
                        assessmentType === 'verbal_ability' ? 'multiple_choice' : 'multiple_choice'
    
    loadQuestionBankQuestions(categoryStr, questionType)
    setShowQuestionBankModal(true)
  }

  const handleCategoryClick = (category: {collegeType: 'engineering' | 'management', tier: 'tier1' | 'tier2'}) => {
    setSelectedCategory(category)
    setShowViewAssessmentModal(false)
    setShowAssessmentTypeModal(true)
  }

  const handleViewAssessmentTypeSelect = (assessmentType: string) => {
    // Navigate to a full page showing questions for this category and type
    const categoryStr = `${selectedCategory?.collegeType}_${selectedCategory?.tier}`
    navigate(`/recruiter/questions/${categoryStr}/${assessmentType}`)
  }

  const getDriveStatusBadge = (status: string) => {
    const statusConfig = {
      upcoming: { color: 'bg-blue-100 text-blue-800', text: 'Upcoming' },
      active: { color: 'bg-green-100 text-green-800', text: 'Active' },
      completed: { color: 'bg-gray-100 text-gray-800', text: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', text: 'Cancelled' }
    }
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.completed
    
    return (
      <span className={clsx('inline-flex items-center px-2 py-1 rounded text-xs font-medium', config.color)}>
        {config.text}
      </span>
    )
  }

  const getCompletionPercentage = (completed: number, total: number): number => {
    return total > 0 ? Math.round((completed / total) * 100) : 0
  }

  const filteredCandidates = candidateResults.filter(candidate => {
    switch (selectedFilter) {
      case 'shortlisted':
        return candidate.status === 'shortlisted'
      case 'pending':
        return candidate.status === 'completed'
      default:
        return true
    }
  })

  const handleViewCandidate = (candidateId: string) => {
    navigate(`/recruiter/candidates/${candidateId}`)
  }

  const handleShortlistCandidate = (candidateId: string) => {
    setCandidateResults(prev =>
      prev.map(candidate =>
        candidate.id === candidateId
          ? { ...candidate, status: 'shortlisted' as const }
          : candidate
      )
    )
    toast.success('Candidate shortlisted successfully')
  }

  const handleExportResults = () => {
    try {
      // Get filtered candidates based on current filter
      const filteredCandidates = selectedFilter === 'all' 
        ? candidateResults
        : selectedFilter === 'shortlisted'
        ? candidateResults.filter(c => c.status === 'shortlisted')
        : candidateResults.filter(c => c.status === 'completed')

      // If no candidates, also export drives data
      if (filteredCandidates.length === 0 && drives.length > 0) {
        // Export drives data
        const drivesData = drives.map(drive => ({
          'Drive Title': drive.title,
          'College Type': drive.collegeType,
          'Tier': drive.tier,
          'Start Date': new Date(drive.startDate).toLocaleDateString(),
          'End Date': new Date(drive.endDate).toLocaleDateString(),
          'Status': drive.status,
          'Registered Candidates': drive.registeredCandidates,
          'Total Assessments': drive.linkedAssessments?.length || 0,
          'Created At': new Date(drive.createdAt).toLocaleDateString()
        }))

        const ws = XLSX.utils.json_to_sheet(drivesData)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Drives')
        
        // Generate filename with timestamp
        const filename = `drives_export_${new Date().toISOString().split('T')[0]}.xlsx`
        XLSX.writeFile(wb, filename)
        
        toast.success(`Exported ${drives.length} drives to ${filename}`)
        return
      }

      if (filteredCandidates.length === 0) {
        toast.error('No data to export')
        return
      }

      // Prepare candidate data for export
      const exportData = filteredCandidates.map((candidate, index) => ({
        'S.No': index + 1,
        'Candidate Name': candidate.name,
        'Email': candidate.email,
        'College': candidate.college,
        'Assessment': candidate.assessmentTitle,
        'Score': candidate.score,
        'Status': candidate.status,
        'Completed At': new Date(candidate.completedAt).toLocaleString()
      }))

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData)
      
      // Set column widths
      const colWidths = [
        { wch: 8 },  // S.No
        { wch: 25 }, // Candidate Name
        { wch: 30 }, // Email
        { wch: 30 }, // College
        { wch: 35 }, // Assessment
        { wch: 10 }, // Score
        { wch: 15 }, // Status
        { wch: 25 }  // Completed At
      ]
      ws['!cols'] = colWidths

      // Create workbook
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Candidate Results')

      // Generate filename with timestamp
      const filterSuffix = selectedFilter !== 'all' ? `_${selectedFilter}` : ''
      const filename = `candidate_results${filterSuffix}_${new Date().toISOString().split('T')[0]}.xlsx`
      
      // Download file
      XLSX.writeFile(wb, filename)
      
      toast.success(`Exported ${filteredCandidates.length} candidate(s) to ${filename}`)
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export data. Please try again.')
    }
  }

  const handleCreateAssessment = (collegeType: 'engineering' | 'management', tier: 'tier1' | 'tier2') => {
    // Open assessment type selection modal instead of navigating directly
    setSelectedCategory({ collegeType, tier })
    setShowAssessmentTypeModal(true)
  }

  const handleNewAssessment = () => {
    setShowAssessmentModal(true)
  }

  const handleViewAssessment = () => {
    setShowViewAssessmentModal(true)
  }

  const handleViewAssessmentDetails = (assessment: any) => {
    setSelectedAssessment(assessment)
    setShowAssessmentDetailsModal(true)
  }

  const handleAssessmentCategorySelect = (collegeType: 'engineering' | 'management', tier: 'tier1' | 'tier2') => {
    setShowAssessmentModal(false)
    setSelectedCategory({ collegeType, tier })
    setShowAssessmentTypeModal(true)
  }

  const handleAssessmentTypeSelect = (assessmentType: 'coding' | 'aptitude' | 'fundamentals' | 'case-study') => {
    setShowAssessmentTypeModal(false)
    // Navigate to assessment creation page with category and type
    navigate('/recruiter/assessments/new', {
      state: { 
        category: selectedCategory,
        assessmentType
      }
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading recruiter dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Recruiter Dashboard
              </h1>
              <p className="text-gray-600 mt-2">
                Manage recruitment drives and review candidates
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <button
                onClick={() => navigate('/recruiter/drives/new')}
                className="btn btn-primary bg-blue-400 hover:bg-blue-500 text-white flex items-center space-x-2"
              >
                <PlusIcon className="w-4 h-4" />
                <span>New Drive</span>
              </button>
              <button
                onClick={() => navigate('/recruiter/question-bank')}
                className="btn btn-primary bg-purple-400 hover:bg-purple-500 text-white flex items-center space-x-2"
              >
                <BookOpenIcon className="w-4 h-4" />
                <span>Question Bank</span>
              </button>
              <button
                onClick={handleViewAssessment}
                className="btn btn-primary bg-green-400 hover:bg-green-500 text-white flex items-center space-x-2"
              >
                <EyeIcon className="w-4 h-4" />
                <span>View Assessment</span>
              </button>
              <button
                onClick={() => navigate('/recruiter/colleges')}
                className="btn btn-primary bg-teal-400 hover:bg-teal-500 text-white flex items-center space-x-2"
              >
                <BuildingOfficeIcon className="w-4 h-4" />
                <span>College Registration</span>
              </button>
              <button onClick={() => navigate('/recruiter/monitoring')} className="btn btn-primary bg-indigo-400 hover:bg-indigo-500 text-white flex items-center space-x-2">
                <TableCellsIcon className="w-4 h-4" /><span>Monitoring Records</span>
              </button>
            </div>
          </div>
        </div>

        {/* Recruiter Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <DashboardCard
            title="Total Candidates"
            value={recruiterStats.totalCandidates.toString()}
            icon={UsersIcon}
            iconColor="text-blue-600"
            bgColor="bg-blue-50"
          />
          <DashboardCard
            title="Active Drives"
            value={recruiterStats.activeDrives.toString()}
            icon={CalendarIcon}
            iconColor="text-success-600"
            bgColor="bg-success-50"
          />
          <DashboardCard
            title="Completed Tests"
            value={recruiterStats.completedAssessments.toString()}
            icon={AcademicCapIcon}
            iconColor="text-purple-600"
            bgColor="bg-purple-50"
          />
        </div>

        {/* Active Drives */}
        <div className="card p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Recruitment Drives
            </h2>
            <button
              onClick={() => navigate('/recruiter/drives')}
              className="btn btn-outline btn-sm"
            >
              View All
            </button>
          </div>
          
          {drives.filter(drive => drive.status === 'active').length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {drives.filter(drive => drive.status === 'active').map((drive) => (
                <div
                  key={drive.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors cursor-pointer"
                  onClick={() => navigate(`/recruiter/drives/${drive.id}`)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-medium text-gray-900 flex-1 mr-2">
                      {drive.title}
                    </h3>
                    {getDriveStatusBadge(drive.status)}
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-2">{drive.description}</p>
                  
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {drive.collegeType === 'engineering' ? 'Engineering' : 'Management'}
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {drive.tier === 'tier1' ? 'Tier 1' : 'Tier 2'}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Progress</span>
                      <span className="font-medium">
                        {drive.registeredCandidates}/{drive.maxCandidates}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full"
                        style={{
                          width: `${getCompletionPercentage(drive.registeredCandidates, drive.maxCandidates)}%`
                        }}
                      />
                    </div>
                    
                    
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">
                        {drive.status === 'upcoming' ? 'Starts' : 'Ends'}
                      </span>
                      <span className="font-medium">
                        {formatDistanceToNow(
                          new Date(drive.status === 'upcoming' ? drive.startDate : drive.endDate),
                          { addSuffix: true }
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <CalendarIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-2">No active drives at the moment</p>
              <p className="text-sm text-gray-400">Click "View All" to see all drives or create a new drive</p>
            </div>
          )}
        </div>

        {/* Question Bank Management */}
        <div className="card p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Question Bank Management
            </h2>
          </div>
          
          <div
            onClick={() => navigate('/recruiter/question-bank')}
            className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gray-300 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-center">
              <div className="p-3 bg-gray-100 rounded-lg mr-4 group-hover:bg-gray-200 transition-colors">
                <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Manage Question Bank</h3>
              </div>
            </div>
          </div>
        </div>

        {/* View Assessments */}
        <div id="view-assessments-section" className="card p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              View Assessments
            </h2>
            <span className="text-sm text-gray-500">Browse assessments by category</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Engineering Tier 1 */}
            <div 
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
              onClick={() => {
                setSelectedCategory({ collegeType: 'engineering', tier: 'tier1' })
                setShowAssessmentTypeModal(true)
              }}
            >
              <div className="flex items-center mb-3">
                <div className="p-2 bg-blue-100 rounded-lg mr-3 group-hover:bg-blue-200 transition-colors">
                  <EyeIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Engineering</h3>
                  <p className="text-sm text-gray-600">Tier 1</p>
                </div>
              </div>
              <div className="flex items-center text-sm text-blue-600 group-hover:text-blue-700">
                <span>View Assessments</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

            {/* Engineering Tier 2 */}
            <div 
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
              onClick={() => {
                setSelectedCategory({ collegeType: 'engineering', tier: 'tier2' })
                setShowAssessmentTypeModal(true)
              }}
            >
              <div className="flex items-center mb-3">
                <div className="p-2 bg-blue-100 rounded-lg mr-3 group-hover:bg-blue-200 transition-colors">
                  <EyeIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Engineering</h3>
                  <p className="text-sm text-gray-600">Tier 2</p>
                </div>
              </div>
              <div className="flex items-center text-sm text-blue-600 group-hover:text-blue-700">
                <span>View Assessments</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

            {/* Management Tier 1 */}
            <div 
              className="border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:shadow-md transition-all cursor-pointer group"
              onClick={() => {
                setSelectedCategory({ collegeType: 'management', tier: 'tier1' })
                setShowAssessmentTypeModal(true)
              }}
            >
              <div className="flex items-center mb-3">
                <div className="p-2 bg-green-100 rounded-lg mr-3 group-hover:bg-green-200 transition-colors">
                  <EyeIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Management</h3>
                  <p className="text-sm text-gray-600">Tier 1</p>
                </div>
              </div>
              <div className="flex items-center text-sm text-green-600 group-hover:text-green-700">
                <span>View Assessments</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

            {/* Management Tier 2 */}
            <div 
              className="border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:shadow-md transition-all cursor-pointer group"
              onClick={() => {
                setSelectedCategory({ collegeType: 'management', tier: 'tier2' })
                setShowAssessmentTypeModal(true)
              }}
            >
              <div className="flex items-center mb-3">
                <div className="p-2 bg-green-100 rounded-lg mr-3 group-hover:bg-green-200 transition-colors">
                  <EyeIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Management</h3>
                  <p className="text-sm text-gray-600">Tier 2</p>
                </div>
              </div>
              <div className="flex items-center text-sm text-green-600 group-hover:text-green-700">
                <span>View Assessments</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Candidate Results (temporarily hidden per recruiter feedback) */}
        {/* Keeping component structure for future re-enable */}
        {false && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Candidate Results
              </h2>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <FunnelIcon className="w-4 h-4 text-gray-500" />
                  <select
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value as any)}
                    className="input w-auto text-sm"
                  >
                    <option value="all">All Candidates</option>
                    <option value="shortlisted">Shortlisted</option>
                    <option value="pending">Pending Review</option>
                  </select>
                </div>
              </div>
            </div>
            
            {filteredCandidates.length === 0 ? (
              <div className="text-center py-12">
                <UsersIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No candidates found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCandidates.map((candidate) => (
                  <CandidateCard
                    key={candidate.id}
                    candidate={candidate}
                    onView={() => handleViewCandidate(candidate.id)}
                    onShortlist={() => handleShortlistCandidate(candidate.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Assessment Category Selection Modal */}
      {showAssessmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Select Assessment Category
              </h3>
              <button
                onClick={() => setShowAssessmentModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
             <p className="text-gray-600 mb-6">
               Choose Category and Create Assessment
             </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Engineering Tier 1 */}
              <div 
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => handleAssessmentCategorySelect('engineering', 'tier1')}
              >
                <div className="flex items-center mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3 group-hover:bg-blue-200 transition-colors">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Engineering</h4>
                    <p className="text-sm text-gray-600">Tier 1</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">IITs & NITs - Advanced problem-solving, coding, analytical</p>
                <div className="flex items-center text-sm text-blue-600 group-hover:text-blue-700">
                  <span>Create Assessment</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Engineering Tier 2 */}
              <div 
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => handleAssessmentCategorySelect('engineering', 'tier2')}
              >
                <div className="flex items-center mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3 group-hover:bg-blue-200 transition-colors">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Engineering</h4>
                    <p className="text-sm text-gray-600">Tier 2</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">Other Engineering - Aptitude, reasoning, technical skills</p>
                <div className="flex items-center text-sm text-blue-600 group-hover:text-blue-700">
                  <span>Create Assessment</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Management Tier 1 */}
              <div 
                className="border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => handleAssessmentCategorySelect('management', 'tier1')}
              >
                <div className="flex items-center mb-3">
                  <div className="p-2 bg-green-100 rounded-lg mr-3 group-hover:bg-green-200 transition-colors">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Management</h4>
                    <p className="text-sm text-gray-600">Tier 1</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">IIMs & Top MBA - Case studies, business simulations</p>
                <div className="flex items-center text-sm text-green-600 group-hover:text-green-700">
                  <span>Create Assessment</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Management Tier 2 */}
              <div 
                className="border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => handleAssessmentCategorySelect('management', 'tier2')}
              >
                <div className="flex items-center mb-3">
                  <div className="p-2 bg-green-100 rounded-lg mr-3 group-hover:bg-green-200 transition-colors">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Management</h4>
                    <p className="text-sm text-gray-600">Tier 2</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">Other MBA - Aptitude, reasoning, communication</p>
                <div className="flex items-center text-sm text-green-600 group-hover:text-green-700">
                  <span>Create Assessment</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowAssessmentModal(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
            </div>
           </div>
         </div>
       )}

       {/* Assessment Type Selection Modal */}
       {showAssessmentTypeModal && selectedCategory && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
             <div className="flex items-center justify-between mb-6">
               <h3 className="text-xl font-semibold text-gray-900">
                 Select Assessment Type
               </h3>
               <button
                 onClick={() => setShowAssessmentTypeModal(false)}
                 className="text-gray-400 hover:text-gray-600 transition-colors"
               >
                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                 </svg>
               </button>
             </div>
             
             <div className="mb-4">
               <p className="text-sm text-gray-600">
                 Category: <span className="font-medium capitalize">{selectedCategory.collegeType} - {selectedCategory.tier === 'tier1' ? 'Tier 1' : 'Tier 2'}</span>
               </p>
             </div>
             
             <p className="text-gray-600 mb-6">
               Choose the assessment type to view questions from the question bank
             </p>
             
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
               {/* Coding Problems - Only for Engineering */}
               {selectedCategory.collegeType === 'engineering' && (
                 <div 
                   className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                   onClick={() => handleViewAssessmentTypeSelect('coding')}
                 >
                   <div className="flex items-center mb-3">
                     <div className="p-2 bg-blue-100 rounded-lg mr-3 group-hover:bg-blue-200 transition-colors">
                       <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                       </svg>
                     </div>
                     <div>
                       <h4 className="font-semibold text-gray-900">Coding Problems</h4>
                     </div>
                   </div>
                   <p className="text-xs text-gray-500 mb-3">
                     Algorithms, data structures, system design problems
                   </p>
                   <div className="flex items-center text-sm text-blue-600 group-hover:text-blue-700">
                     <span>View Questions</span>
                     <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                     </svg>
                   </div>
                 </div>
               )}

               {/* Quantitative Ability - Only for Management */}
               {selectedCategory.collegeType === 'management' && (
                 <div
                   className="border border-gray-200 rounded-lg p-4 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer group"
                   onClick={() => handleViewAssessmentTypeSelect('quantitative_ability')}
                 >
                   <div className="flex items-center mb-3">
                     <div className="p-2 bg-orange-100 rounded-lg mr-3 group-hover:bg-orange-200 transition-colors">
                       <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                       </svg>
                     </div>
                     <div>
                       <h4 className="font-semibold text-gray-900">Quantitative Ability</h4>
                     </div>
                   </div>
                   <p className="text-xs text-gray-500 mb-3">
                     Numerical and mathematical problem-solving skills
                   </p>
                   <div className="flex items-center text-sm text-orange-600 group-hover:text-orange-700">
                     <span>View Questions</span>
                     <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                     </svg>
                   </div>
                 </div>
               )}

               {/* Logical Reasoning - Only for Management */}
               {selectedCategory.collegeType === 'management' && (
                 <div
                   className="border border-gray-200 rounded-lg p-4 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer group"
                   onClick={() => handleViewAssessmentTypeSelect('logical_reasoning')}
                 >
                   <div className="flex items-center mb-3">
                     <div className="p-2 bg-teal-100 rounded-lg mr-3 group-hover:bg-teal-200 transition-colors">
                       <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                       </svg>
                     </div>
                     <div>
                       <h4 className="font-semibold text-gray-900">Logical Reasoning</h4>
                     </div>
                   </div>
                   <p className="text-xs text-gray-500 mb-3">
                     Critical thinking and logical deduction abilities
                   </p>
                   <div className="flex items-center text-sm text-teal-600 group-hover:text-teal-700">
                     <span>View Questions</span>
                     <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                     </svg>
                   </div>
                 </div>
               )}

               {/* Verbal Ability - Only for Management */}
               {selectedCategory.collegeType === 'management' && (
                 <div
                   className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer group"
                   onClick={() => handleViewAssessmentTypeSelect('verbal_ability')}
                 >
                   <div className="flex items-center mb-3">
                     <div className="p-2 bg-purple-100 rounded-lg mr-3 group-hover:bg-purple-200 transition-colors">
                       <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                       </svg>
                     </div>
                     <div>
                       <h4 className="font-semibold text-gray-900">Verbal Ability</h4>
                     </div>
                   </div>
                   <p className="text-xs text-gray-500 mb-3">
                     English language comprehension and communication
                   </p>
                   <div className="flex items-center text-sm text-purple-600 group-hover:text-purple-700">
                     <span>View Questions</span>
                     <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                     </svg>
                   </div>
                 </div>
               )}

              {/* Aptitude and Reasoning - Only for Engineering */}
              {selectedCategory.collegeType === 'engineering' && (
              <div 
                className="border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => handleViewAssessmentTypeSelect('aptitude')}
              >
                <div className="flex items-center mb-3">
                  <div className="p-2 bg-green-100 rounded-lg mr-3 group-hover:bg-green-200 transition-colors">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Aptitude & Reasoning</h4>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Logical reasoning, quantitative aptitude, technical reasoning
                </p>
                <div className="flex items-center text-sm text-green-600 group-hover:text-green-700">
                  <span>View Questions</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
              )}

             {/* Subject Fundamentals - Only for Engineering */}
             {selectedCategory.collegeType === 'engineering' && (
             <div 
               className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer group"
               onClick={() => handleViewAssessmentTypeSelect('fundamentals')}
             >
               <div className="flex items-center mb-3">
                 <div className="p-2 bg-purple-100 rounded-lg mr-3 group-hover:bg-purple-200 transition-colors">
                   <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                   </svg>
                 </div>
                 <div>
                   <h4 className="font-semibold text-gray-900">Computer Fundamentals</h4>
                 </div>
               </div>
               <p className="text-xs text-gray-500 mb-3">
                 Core computer science knowledge questions
               </p>
               <div className="flex items-center text-sm text-purple-600 group-hover:text-purple-700">
                 <span>View Questions</span>
                 <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                 </svg>
               </div>
             </div>
             )}

              {/* Cloud Fundamentals - Only for Engineering */}
              {selectedCategory.collegeType === 'engineering' && (
                <div 
                  className="border border-gray-200 rounded-lg p-4 hover:border-sky-300 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => handleViewAssessmentTypeSelect('cloud_fundamentals')}
                >
                  <div className="flex items-center mb-3">
                    <div className="p-2 bg-sky-100 rounded-lg mr-3 group-hover:bg-sky-200 transition-colors">
                      <svg className="w-6 h-6 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Cloud Fundamentals</h4>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Cloud computing and infrastructure knowledge
                  </p>
                  <div className="flex items-center text-sm text-sky-600 group-hover:text-sky-700">
                    <span>View Questions</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Pre-Sales Fundamentals - Only for Engineering */}
              {selectedCategory.collegeType === 'engineering' && (
                <div 
                  className="border border-gray-200 rounded-lg p-4 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => handleViewAssessmentTypeSelect('presales_fundamentals')}
                >
                  <div className="flex items-center mb-3">
                    <div className="p-2 bg-orange-100 rounded-lg mr-3 group-hover:bg-orange-200 transition-colors">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Pre-Sales Fundamentals</h4>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Pre-sales and solution engineering knowledge
                  </p>
                  <div className="flex items-center text-sm text-orange-600 group-hover:text-orange-700">
                    <span>View Questions</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              )}

              {/* English Communication - Only for Engineering */}
              {selectedCategory.collegeType === 'engineering' && (
                <div 
                  className="border border-gray-200 rounded-lg p-4 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => handleViewAssessmentTypeSelect('english_communication')}
                >
                  <div className="flex items-center mb-3">
                    <div className="p-2 bg-teal-100 rounded-lg mr-3 group-hover:bg-teal-200 transition-colors">
                      <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">English Communication</h4>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    English language proficiency and communication skills
                  </p>
                  <div className="flex items-center text-sm text-teal-600 group-hover:text-teal-700">
                    <span>View Questions</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              )}

              {/* AI Fundamental - Only for Engineering */}
              {selectedCategory.collegeType === 'engineering' && (
                <div 
                  className="border border-gray-200 rounded-lg p-4 hover:border-violet-300 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => handleViewAssessmentTypeSelect('ai_fundamental')}
                >
                  <div className="flex items-center mb-3">
                    <div className="p-2 bg-violet-100 rounded-lg mr-3 group-hover:bg-violet-200 transition-colors">
                      <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">AI Fundamental</h4>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Artificial Intelligence fundamentals and core concepts
                  </p>
                  <div className="flex items-center text-sm text-violet-600 group-hover:text-violet-700">
                    <span>View Questions</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              )}

              {/* AI/ML Assignment - Only for Engineering */}
              {selectedCategory.collegeType === 'engineering' && (
                <div 
                  className="border border-gray-200 rounded-lg p-4 hover:border-pink-300 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => handleViewAssessmentTypeSelect('aiml_assignment')}
                >
                  <div className="flex items-center mb-3">
                    <div className="p-2 bg-pink-100 rounded-lg mr-3 group-hover:bg-pink-200 transition-colors">
                      <svg className="w-6 h-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">AI/ML Assignment</h4>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    AI/ML project assignments with link submission
                  </p>
                  <div className="flex items-center text-sm text-pink-600 group-hover:text-pink-700">
                    <span>View Questions</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
             
             <div className="flex justify-between mt-6">
               <button
                 onClick={() => {
                   setShowAssessmentTypeModal(false)
                   setShowAssessmentModal(true)
                 }}
                 className="btn btn-outline"
               >
                 Back
               </button>
               <button
                 onClick={() => setShowAssessmentTypeModal(false)}
                 className="btn btn-outline"
               >
                 Cancel
               </button>
             </div>
           </div>
         </div>
       )}

       {/* View Assessment Modal */}
       {showViewAssessmentModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
             <div className="flex items-center justify-between mb-6">
               <h3 className="text-xl font-semibold text-gray-900">
                 View Assessments
               </h3>
               <button
                 onClick={() => setShowViewAssessmentModal(false)}
                 className="text-gray-400 hover:text-gray-600 transition-colors"
               >
                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                 </svg>
               </button>
             </div>
             
             <p className="text-gray-600 mb-6">
               View all created assessments organized by category
             </p>
             
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
               {/* Engineering Tier 1 */}
               <div 
                 className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                 onClick={() => handleCategoryClick({ collegeType: 'engineering', tier: 'tier1' })}
               >
                 <div className="flex items-center mb-3">
                   <div className="p-2 bg-blue-100 rounded-lg mr-3 group-hover:bg-blue-200 transition-colors">
                     <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                     </svg>
                   </div>
                   <div>
                     <h4 className="font-semibold text-gray-900">Engineering</h4>
                     <p className="text-sm text-gray-600">Tier 1</p>
                   </div>
                 </div>
                 <div className="text-center py-4">
                   <p className="text-sm text-gray-500 mb-3">Click to view assessment types</p>
                   <div className="flex items-center text-blue-600 font-medium group-hover:translate-x-1 transition-transform">
                     <span>Select Category</span>
                     <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                     </svg>
                   </div>
                 </div>
               </div>

               {/* Engineering Tier 2 */}
               <div 
                 className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                 onClick={() => handleCategoryClick({ collegeType: 'engineering', tier: 'tier2' })}
               >
                 <div className="flex items-center mb-3">
                   <div className="p-2 bg-blue-100 rounded-lg mr-3 group-hover:bg-blue-200 transition-colors">
                     <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                     </svg>
                   </div>
                   <div>
                     <h4 className="font-semibold text-gray-900">Engineering</h4>
                     <p className="text-sm text-gray-600">Tier 2</p>
                   </div>
                 </div>
                 <div className="text-center py-4">
                   <p className="text-sm text-gray-500 mb-3">Click to view assessment types</p>
                   <div className="flex items-center text-blue-600 font-medium group-hover:translate-x-1 transition-transform">
                     <span>Select Category</span>
                     <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                     </svg>
                   </div>
                 </div>
               </div>

               {/* Management Tier 1 */}
               <div 
                 className="border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:shadow-md transition-all cursor-pointer group"
                 onClick={() => handleCategoryClick({ collegeType: 'management', tier: 'tier1' })}
               >
                 <div className="flex items-center mb-3">
                   <div className="p-2 bg-green-100 rounded-lg mr-3 group-hover:bg-green-200 transition-colors">
                     <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                     </svg>
                   </div>
                   <div>
                     <h4 className="font-semibold text-gray-900">Management</h4>
                     <p className="text-sm text-gray-600">Tier 1</p>
                   </div>
                 </div>
                 <div className="text-center py-4">
                   <p className="text-sm text-gray-500 mb-3">Click to view assessment types</p>
                   <div className="flex items-center text-green-600 font-medium group-hover:translate-x-1 transition-transform">
                     <span>Select Category</span>
                     <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                     </svg>
                   </div>
                 </div>
               </div>

               {/* Management Tier 2 */}
               <div 
                 className="border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:shadow-md transition-all cursor-pointer group"
                 onClick={() => handleCategoryClick({ collegeType: 'management', tier: 'tier2' })}
               >
                 <div className="flex items-center mb-3">
                   <div className="p-2 bg-green-100 rounded-lg mr-3 group-hover:bg-green-200 transition-colors">
                     <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                     </svg>
                   </div>
                   <div>
                     <h4 className="font-semibold text-gray-900">Management</h4>
                     <p className="text-sm text-gray-600">Tier 2</p>
                   </div>
                 </div>
                 <div className="text-center py-4">
                   <p className="text-sm text-gray-500 mb-3">Click to view assessment types</p>
                   <div className="flex items-center text-green-600 font-medium group-hover:translate-x-1 transition-transform">
                     <span>Select Category</span>
                     <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                     </svg>
                   </div>
                 </div>
               </div>
             </div>
             
             <div className="flex justify-end mt-6">
               <button
                 onClick={() => setShowViewAssessmentModal(false)}
                 className="btn btn-outline"
               >
                 Close
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Assessment Details Modal */}
       {showAssessmentDetailsModal && selectedAssessment && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
             <div className="flex items-center justify-between mb-6">
               <h3 className="text-xl font-semibold text-gray-900">
                 Assessment Details
               </h3>
               <button
                 onClick={() => setShowAssessmentDetailsModal(false)}
                 className="text-gray-400 hover:text-gray-600 transition-colors"
               >
                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                 </svg>
               </button>
             </div>
             
             {/* Assessment Header */}
             <div className="bg-gray-50 rounded-lg p-4 mb-6">
               <div className="flex items-center justify-between mb-2">
                 <h4 className="text-lg font-semibold text-gray-900">{selectedAssessment.title}</h4>
                 <span className="text-sm text-gray-600 capitalize">{selectedAssessment.assessmentType}</span>
               </div>
               <p className="text-gray-600 mb-2">{selectedAssessment.description}</p>
               <div className="flex items-center space-x-4 text-sm text-gray-500">
                 <span>Duration: {selectedAssessment.duration} minutes</span>
                 <span>Problems: {selectedAssessment.problems?.length || 0}</span>
                 <span>Category: {selectedAssessment.category?.collegeType} - {selectedAssessment.category?.tier}</span>
               </div>
             </div>

             {/* Problems List */}
             <div className="space-y-4">
               <h5 className="text-lg font-medium text-gray-900">Problems</h5>
               {selectedAssessment.problems && selectedAssessment.problems.length > 0 ? (
                 selectedAssessment.problems.map((problem: any, index: number) => (
                   <div key={problem.id || index} className="border border-gray-200 rounded-lg p-4">
                     <div className="flex items-center justify-between mb-3">
                       <div className="flex-1">
                         <h6 className="font-medium text-gray-900 text-lg">
                           {problem.title || `Problem ${index + 1}`}
                         </h6>
                         {problem.title && (
                           <p className="text-sm text-gray-500">Problem #{index + 1}</p>
                         )}
                       </div>
                       <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                         {problem.points || 0} points
                       </span>
                     </div>
                     
                     <div className="space-y-3">
                       <div>
                         <label className="block text-sm font-medium text-gray-700 mb-1">Problem Statement</label>
                         <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{problem.description}</p>
                       </div>

                       {selectedAssessment.assessmentType === 'coding' && (
                         <>
                           {problem.testCases && problem.testCases.length > 0 && (
                             <div>
                               <label className="block text-sm font-medium text-gray-700 mb-1">Test Cases</label>
                               <div className="space-y-2">
                                 {problem.testCases.map((testCase: any, tcIndex: number) => (
                                   <div key={tcIndex} className="bg-gray-50 p-3 rounded">
                                     <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                       <div>
                                         <span className="text-xs font-medium text-gray-500">Input:</span>
                                         <p className="text-sm text-gray-600 font-mono">{testCase.input}</p>
                                       </div>
                                       <div>
                                         <span className="text-xs font-medium text-gray-500">Expected Output:</span>
                                         <p className="text-sm text-gray-600 font-mono">{testCase.expectedOutput}</p>
                                       </div>
                                     </div>
                                     {testCase.isHidden && (
                                       <span className="inline-block mt-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                         Hidden Test Case
                                       </span>
                                     )}
                                   </div>
                                 ))}
                               </div>
                             </div>
                           )}
                         </>
                       )}

                       {selectedAssessment.assessmentType === 'aptitude' && (
                         <>
                           {problem.options && problem.options.length > 0 && (
                             <div>
                               <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                               <div className="space-y-2">
                                 {problem.options.map((option: any, optIndex: number) => (
                                   <div key={optIndex} className={`p-2 rounded ${option.isCorrect ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                                     <span className="text-sm font-medium text-gray-600">{String.fromCharCode(65 + optIndex)}. </span>
                                     <span className="text-sm text-gray-600">{option.text}</span>
                                     {option.isCorrect && (
                                       <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                         Correct
                                       </span>
                                     )}
                                   </div>
                                 ))}
                               </div>
                             </div>
                           )}
                         </>
                       )}

                       {selectedAssessment.assessmentType === 'fundamentals' && (
                         <>
                           {problem.options && problem.options.length > 0 && (
                             <div>
                               <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                               <div className="space-y-2">
                                 {problem.options.map((option: any, optIndex: number) => (
                                   <div key={optIndex} className={`p-2 rounded ${option.isCorrect ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                                     <span className="text-sm font-medium text-gray-600">{String.fromCharCode(65 + optIndex)}. </span>
                                     <span className="text-sm text-gray-600">{option.text}</span>
                                     {option.isCorrect && (
                                       <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                         Correct
                                       </span>
                                     )}
                                   </div>
                                 ))}
                               </div>
                             </div>
                           )}
                         </>
                       )}

                       {selectedAssessment.assessmentType === 'case-study' && (
                         <>
                           <div>
                             <label className="block text-sm font-medium text-gray-700 mb-1">Additional Details</label>
                             <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                               Case study problems are described in the problem statement above.
                             </p>
                           </div>
                         </>
                       )}
                     </div>
                   </div>
                 ))
               ) : (
                 <div className="text-center py-8">
                   <p className="text-gray-500">No problems added to this assessment yet</p>
                 </div>
               )}
             </div>
             
             <div className="flex justify-end mt-6">
               <button
                 onClick={() => setShowAssessmentDetailsModal(false)}
                 className="btn btn-outline"
               >
                 Close
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Question Bank Modal */}
       {showQuestionBankModal && selectedCategory && selectedAssessmentType && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
             <div className="flex items-center justify-between mb-6">
               <h3 className="text-xl font-semibold text-gray-900">
                 Question Bank - {selectedCategory.collegeType.charAt(0).toUpperCase() + selectedCategory.collegeType.slice(1)} {selectedCategory.tier === 'tier1' ? 'Tier 1' : 'Tier 2'} - {selectedAssessmentType.charAt(0).toUpperCase() + selectedAssessmentType.slice(1)}
               </h3>
               <button
                 onClick={() => setShowQuestionBankModal(false)}
                 className="text-gray-400 hover:text-gray-600 transition-colors"
               >
                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                 </svg>
               </button>
             </div>
             
             <div className="mb-4">
               <p className="text-sm text-gray-600">
                 Showing all questions from the question bank for this category and type
               </p>
             </div>

             {questionBankQuestions.length > 0 ? (
               <div className="space-y-4">
                 {questionBankQuestions.map((question, index) => (
                   <div key={question.id || index} className="border border-gray-200 rounded-lg p-4">
                     <div className="flex items-center justify-between mb-3">
                       <div className="flex-1">
                         <h6 className="font-medium text-gray-900 text-lg">
                           {question.title || `Question ${index + 1}`}
                         </h6>
                         <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                           <span>Difficulty: {question.difficulty_level}</span>
                           <span>Marks: {question.marks}</span>
                           {question.time_limit_minutes && (
                             <span>Time: {question.time_limit_minutes} min</span>
                           )}
                         </div>
                       </div>
                       <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                         #{index + 1}
                       </span>
                     </div>
                     
                     <div className="space-y-3">
                       <div>
                         <label className="block text-sm font-medium text-gray-700 mb-1">Question Statement</label>
                         <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                           {question.description || question.question_data?.problem_statement || 'No description available'}
                         </p>
                       </div>

                       {selectedAssessmentType === 'coding' && question.question_data?.test_cases && (
                         <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">Test Cases</label>
                           <div className="space-y-2">
                             {question.question_data.test_cases.slice(0, 3).map((testCase: any, tcIndex: number) => (
                               <div key={tcIndex} className="bg-gray-50 p-3 rounded">
                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                   <div>
                                     <span className="text-xs font-medium text-gray-500">Input:</span>
                                     <p className="text-sm text-gray-600 font-mono">{testCase.input}</p>
                                   </div>
                                   <div>
                                     <span className="text-xs font-medium text-gray-500">Expected Output:</span>
                                     <p className="text-sm text-gray-600 font-mono">{testCase.expected_output}</p>
                                   </div>
                                 </div>
                               </div>
                             ))}
                             {question.question_data.test_cases.length > 3 && (
                               <p className="text-xs text-gray-500">... and {question.question_data.test_cases.length - 3} more test cases</p>
                             )}
                           </div>
                         </div>
                       )}

                       {(selectedAssessmentType === 'case-study' || selectedAssessmentType === 'business-simulations') && question.question_data?.analysis_questions && (
                         <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">Analysis Questions</label>
                           <div className="space-y-2">
                             {question.question_data.analysis_questions.map((questionText: string, qIndex: number) => (
                               <div key={qIndex} className="bg-gray-50 p-3 rounded">
                                 <span className="text-sm font-medium text-gray-600">{qIndex + 1}. </span>
                                 <span className="text-sm text-gray-600">{questionText}</span>
                               </div>
                             ))}
                           </div>
                           {question.question_data.expected_analysis && (
                             <div className="mt-4">
                               <label className="block text-sm font-medium text-gray-700 mb-1">Expected Analysis Points</label>
                               <div className="bg-green-50 p-3 rounded">
                                 <p className="text-sm text-gray-600">{question.question_data.expected_analysis}</p>
                               </div>
                             </div>
                           )}
                         </div>
                       )}

                      {(selectedAssessmentType === 'aptitude' ||
                        selectedAssessmentType === 'fundamentals' ||
                        selectedAssessmentType === 'cloud_fundamentals' ||
                        selectedAssessmentType === 'presales_fundamentals' ||
                        selectedAssessmentType === 'english_communication' ||
                        selectedAssessmentType === 'ai_fundamental' ||
                        selectedAssessmentType === 'quantitative_ability' ||
                        selectedAssessmentType === 'logical_reasoning' ||
                        selectedAssessmentType === 'verbal_ability') && question.options && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                          <div className="space-y-2">
                            {/* Options can be a dict {A: '...'} or an array [{id/value: 'A', text: '...'}] */}
                            {Array.isArray(question.options)
                              ? question.options.map((opt: any, i: number) => {
                                  const key = opt.id || opt.value || String.fromCharCode(65 + i)
                                  const val = opt.text || opt.label || ''
                                  const isCorrect = question.correct_answer?.answer === key
                                  return (
                                    <div key={key} className="bg-gray-50 p-2 rounded">
                                      <span className="text-sm font-medium text-gray-600">{key}. </span>
                                      <span className="text-sm text-gray-600">{val}</span>
                                      {isCorrect && (
                                        <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Correct</span>
                                      )}
                                    </div>
                                  )
                                })
                              : Object.entries(question.options).map(([key, value]: [string, any]) => (
                                <div key={key} className="bg-gray-50 p-2 rounded">
                                  <span className="text-sm font-medium text-gray-600">{key}. </span>
                                  <span className="text-sm text-gray-600">{value}</span>
                                  {question.correct_answer?.answer === key && (
                                    <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                      Correct
                                    </span>
                                  )}
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      )}
                     </div>
                   </div>
                 ))}
               </div>
             ) : (
               <div className="text-center py-8">
                 <p className="text-gray-500">No questions found</p>
               </div>
             )}
             
             <div className="flex justify-end mt-6">
               <button
                 onClick={() => setShowQuestionBankModal(false)}
                 className="btn btn-outline"
               >
                 Close
               </button>
             </div>
           </div>
         </div>
       )}
     </div>
   )
 }

export default RecruiterDashboard
